import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, type Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { getErrorText } from '@common/utils';
import { defer, exhaustMap, merge, Observable, throwError, timer } from 'rxjs';
import { catchError, debounceTime, retry, startWith, switchMap, tap } from 'rxjs/operators';

const debounceTimeMs = 300;
const maxAttempts = 4;
const retryDelayMultiplier = 1000;

@Injectable()
export class IntervalAutoFetchService {
  readonly #autoFetchError = signal<string>('');
  readonly #refreshTrigger = signal<void>(null);

  private readonly forceRefresh$ = toObservable(this.#refreshTrigger);

  public readonly updateInterval = new FormControl<number>(30, {
    nonNullable: true,
    validators: [Validators.min(1)]
  });

  get autoFetchError(): Signal<string> {
    return this.#autoFetchError.asReadonly();
  }

  setAutoFetchError(error: string): void {
    this.#autoFetchError.set(error);
  }

  /*
   * Manually triggers an immediate fetch
   */
  public forceFetch(): void {
    this.#refreshTrigger.set();
  }

  getAutoFetch<TResult>(request$: Observable<TResult>): Observable<TResult> {
    return this.updateInterval.valueChanges.pipe(
      startWith(this.updateInterval.value),
      debounceTime(debounceTimeMs),
      switchMap((intervalSec) => this.createFetchObservable(intervalSec, request$))
    );
  }

  private createFetchObservable<TResult>(intervalSec: number, request$: Observable<TResult>): Observable<TResult> {
    return merge(this.forceRefresh$, timer(0, intervalSec * retryDelayMultiplier)).pipe(
      exhaustMap(() => this.executeRequestWithRetry(request$))
    );
  }

  private executeRequestWithRetry<TResult>(request$: Observable<TResult>): Observable<TResult> {
    return defer(() => request$).pipe(
      retry({
        count: maxAttempts,
        delay: (error, retryCount) => this.calculateRetryDelay(error, retryCount),
        resetOnSuccess: true
      }),
      tap({
        next: () => this.handleSuccess(),
        error: (err) => this.handleError(err)
      }),
      catchError((err) => throwError(() => err))
    );
  }

  private calculateRetryDelay(error: HttpErrorResponse, retryCount: number): Observable<number> {
    const delayMs = retryCount * this.updateInterval.value * retryDelayMultiplier;

    this.handleError(error);

    return timer(delayMs);
  }

  private handleSuccess(): void {
    this.#autoFetchError.set('');
  }

  private handleError(res: HttpErrorResponse): void {
    const errorMessage = getErrorText(res);

    this.#autoFetchError.set(errorMessage);
  }
}
